import { DOCS_URLS, getEnvironment } from "@p0security/frontend-core";
import { getEnvGcipConfig } from "@p0security/frontend-core/react";
import {
  gatewayUrlOf,
  loadBalancerIpOf,
} from "@p0security/integrations-agentic-shared/domain-hosting";
import {
  DNS_RECORD_TYPE_BY_KIND,
  classifyLoadBalancerAddress,
} from "@p0security/integrations-agentic-shared/network";
import {
  DEFAULT_KUBERNETES_NAMESPACE,
  GOOGLE_OIDC_ISSUER,
  type GatewayLogin,
  type GatewayStorage,
  gatewayDeploymentTerraform,
  installedFederatedIssuers,
} from "@p0security/integrations-agentic-shared/terraform";
import { isPasswordLoginOrg } from "@p0security/integrations-agentic-shared/util";
import { Alert, Form, Input, Select, Typography } from "@p0security/ui";
import { validate as validateEmail } from "isemail";
import type { ChangeEvent, ReactNode } from "react";

import { CommandDisplay } from "./CommandDisplay.tsx";
import { agenticLogProviderFieldInstaller } from "./shared.tsx";
import type { AgenticInstaller } from "./types.ts";

const { Link, Text } = Typography;

type GatewayInstaller = AgenticInstaller["components"]["gateway"];

type GatewayContext = Parameters<
  NonNullable<GatewayInstaller["instructions"]>
>[0];

const isGcipLogin = (context: GatewayContext) =>
  isPasswordLoginOrg(context.orgData);

// No persisted record of the namespace a gateway was actually deployed
// into — this literal placeholder stands in for it, same as `<gateway-host>`
// elsewhere in the docs. The reader swaps in their own (`p0-agentic-gateway`
// unless they set a custom one).
const NAMESPACE_PLACEHOLDER = "<namespace>";

const readLoadBalancerAddressCommand = (namespace: string) =>
  `kubectl -n ${namespace} get gateway agentic-gateway -o jsonpath='{.status.addresses[0].value}'`;

const namespaceReplacementNote = (
  <p>
    Replace <Text code>{NAMESPACE_PLACEHOLDER}</Text> with the namespace you
    deployed the gateway into.
  </p>
);

const EXTRACT_BRAEKHUS_PUBLIC_KEY_COMMAND = `kubectl -n ${NAMESPACE_PLACEHOLDER} exec deploy/braekhus-client -- cat /p0-files/jwk.public.json`;

const describeBraekhusPublicJwk = () => (
  <>
    <p>
      Run this on the gateway to read the outbound management tunnel public key:
    </p>
    <CommandDisplay
      commands={EXTRACT_BRAEKHUS_PUBLIC_KEY_COMMAND}
      minRows={1}
    />
    {namespaceReplacementNote}
    <p>Paste its output above.</p>
  </>
);

const describeLoadBalancerAddress = (value: string | undefined) => {
  const kind = classifyLoadBalancerAddress(value);
  const dnsInstruction = !kind
    ? "Create a DNS record for your configured Agentic gateway URL pointing to this address with your DNS provider: " +
      "an A record for an IPv4 address, AAAA for IPv6, or CNAME if your cloud provider (e.g. AWS) gave you a hostname instead of a static IP."
    : `Create a ${DNS_RECORD_TYPE_BY_KIND[kind]} record for your configured Agentic gateway URL pointing to this ${
        kind === "hostname" ? "hostname" : "address"
      } with your DNS provider.${
        kind === "hostname"
          ? " If your gateway's hostname is a bare domain (a \"zone apex\"), your DNS provider likely can't accept a CNAME there — use its ALIAS/ANAME equivalent, or a CNAME-flattening feature, instead."
          : ""
      }`;

  return (
    <>
      <p>Run this to read back the gateway&apos;s LoadBalancer address:</p>
      <CommandDisplay
        commands={readLoadBalancerAddressCommand(NAMESPACE_PLACEHOLDER)}
        minRows={1}
      />
      {namespaceReplacementNote}
      <p>{dnsInstruction}</p>
    </>
  );
};

// P0 creates the DNS record itself, regardless of address format, so there's
// nothing here to branch on the format like `describeLoadBalancerAddress` does.
const describeP0HostedLoadBalancerAddress = (hostedDomain: string) => (
  <>
    <p>Run this to read back the gateway&apos;s LoadBalancer address:</p>
    <CommandDisplay
      commands={readLoadBalancerAddressCommand(NAMESPACE_PLACEHOLDER)}
      minRows={1}
    />
    {namespaceReplacementNote}
    <p>
      P0 will automatically create the DNS record for{" "}
      <Text code>{hostedDomain}</Text> pointing to this address.
    </p>
  </>
);

// Replaces the schema's plain-string description, which can't carry a link.
// EKS Auto Mode clusters list a `gp2` class that belongs to the in-tree
// provisioner Auto Mode doesn't run, so claims against it never bind and the
// pods sit in `Pending` with no error to go on. Point those readers at the
// docs rather than explaining the whole trap in a form field.
// One class serves all three volumes, and Valkey's holds credentials, so the
// class has to be encrypted. GKE and AKS disks always are; on EKS only a class
// that sets `encrypted: "true"` is, which the docs' `gp3` class does.
const storageClassNameHelp = (
  <>
    An encrypted block storage class your Kubernetes cluster provides for the
    gateway&apos;s bundled PostgreSQL, VictoriaLogs, and Valkey volumes:{" "}
    <Text code>standard-rwo</Text> on GKE, <Text code>managed-csi</Text> on AKS,
    or on EKS a <Text code>gp3</Text> class that sets{" "}
    <Text code>encrypted: &quot;true&quot;</Text>. Valkey&apos;s volume holds
    credentials, so the class must encrypt it. On an EKS cluster in Auto Mode
    you must create a storage class yourself first — see{" "}
    <Link
      href={DOCS_URLS.AGENTIC_GATEWAY_EKS_AUTO_MODE_STORAGE}
      target="_blank"
      rel="noreferrer"
    >
      EKS Auto Mode storage
    </Link>
    .
  </>
);

/** Classes known to provision unencrypted volumes. EKS's stock `gp2` sets no
 * `encrypted` parameter, so its volumes are encrypted only if the AWS account
 * turned on EBS encryption by default. That can't be checked from here, so
 * this warns rather than blocks. */
const UNENCRYPTED_STORAGE_CLASSES = new Set(["gp2"]);

const unencryptedStorageClassWarning = (name: string) => (
  <Alert
    type="warning"
    showIcon
    message={
      <>
        <Text code>{name}</Text> is not encrypted unless your AWS account
        enables EBS encryption by default. Valkey&apos;s volume holds
        credentials, so use an encrypted class instead.
      </>
    }
  />
);

// These values are interpolated into double-quoted HCL strings in the
// generated Terraform — reject characters that could break out of the
// string (quotes, backslashes, `$` for interpolation, `%` for directives)
// or, for the namespace and storage class, that Kubernetes itself would
// reject.
const HCL_UNSAFE_PATTERN = /["\\$%]/;
const OIDC_CLIENT_ID_PATTERN = /^[^"\\$%\s]+$/;
// Also rejects `%`, which starts a `%{...}` template directive.
const OIDC_ISSUER_PATTERN = /^https:\/\/[^"\\$%\s]+$/;
// A DNS-1123 subdomain, as Kubernetes requires of StorageClass names.
const STORAGE_CLASS_NAME_PATTERN =
  /^(?=.{1,253}$)[a-z0-9]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/;

/** isemail accepts RFC 5322 quoted local parts (`"a b"@example.com`), so the
 * HCL check still applies. `minDomainAtoms: 2` rejects dotless domains, which
 * Let's Encrypt can't deliver to. The email keeps `'`, which real addresses
 * use and HCL treats as plain text. */
const isValidEmail = (value: string) =>
  validateEmail(value, { minDomainAtoms: 2 }) &&
  !HCL_UNSAFE_PATTERN.test(value);

const matching = (pattern: RegExp) => (value: string) => pattern.test(value);

type DeploymentField =
  | "kubernetesNamespace"
  | "letsEncryptEmail"
  | "oidcClientId"
  | "oidcIssuer"
  | "storageClassName";

type DeploymentValues = Record<DeploymentField, string> & {
  storageType: GatewayStorage["type"];
};

type DeploymentFieldRule = {
  message: string;
  optional: boolean;
  isValid: (value: string) => boolean;
};

/** Each text input's validity rule, read by both its inline error and the
 * Terraform gate. An `optional` field also passes when empty. */
const DEPLOYMENT_FIELD_RULES: Record<DeploymentField, DeploymentFieldRule> = {
  kubernetesNamespace: {
    message: "Enter a valid Kubernetes namespace.",
    optional: true,
    isValid: matching(/^[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?$/),
  },
  letsEncryptEmail: {
    message: "Enter a valid email address.",
    optional: false,
    isValid: isValidEmail,
  },
  oidcClientId: {
    message: "Enter a valid client ID.",
    optional: false,
    isValid: matching(OIDC_CLIENT_ID_PATTERN),
  },
  oidcIssuer: {
    message: "Enter a valid https:// issuer URL.",
    optional: true,
    isValid: matching(OIDC_ISSUER_PATTERN),
  },
  storageClassName: {
    message: "Enter a valid storage class name.",
    optional: false,
    isValid: matching(STORAGE_CLASS_NAME_PATTERN),
  },
};

/** Each deployment input's value from ephemeral state, normalized for
 * `isFieldValid`, `fieldValidation`, and `gatewayStorageFrom`. */
const deploymentValuesFromState = (
  state: Readonly<Record<string, any>>
): DeploymentValues => ({
  kubernetesNamespace: String(state.kubernetesNamespace ?? "").trim(),
  // Not trimmed: isValidEmail rejects surrounding whitespace, so padding is
  // flagged rather than silently dropped.
  letsEncryptEmail: String(state.letsEncryptEmail ?? ""),
  oidcClientId: String(state.oidcClientId ?? "").trim(),
  oidcIssuer: String(state.oidcIssuer ?? "").trim(),
  storageClassName: String(state.storageClassName ?? "").trim(),
  storageType: state.storageType ?? "storageClass",
});

const isFieldValid = (values: DeploymentValues, field: DeploymentField) => {
  const { isValid, optional } = DEPLOYMENT_FIELD_RULES[field];
  const value = values[field];
  return (optional && value === "") || isValid(value);
};

/** `Form.Item` props that show `field`'s error when it's filled in but
 * invalid. An empty field gets no inline error. */
const fieldValidation = (values: DeploymentValues, field: DeploymentField) =>
  values[field] !== "" && !isFieldValid(values, field)
    ? {
        help: DEPLOYMENT_FIELD_RULES[field].message,
        validateStatus: "error" as const,
      }
    : {};

const FieldDescription = ({ children }: { children: ReactNode }) => (
  <div className="p0-description">
    <Text type="secondary">{children}</Text>
  </div>
);

const STORAGE_TYPE_OPTIONS = [
  { value: "storageClass", label: "Kubernetes storage class" },
  {
    value: "noPersistence",
    label: "No persistent storage (proof of concept only)",
  },
];

// The bundled Valkey is off without a storage class, so the auth server caches
// in memory, and a restarted pod forgets every OAuth credential it held.
const noPersistenceWarning = (
  <Alert
    type="warning"
    showIcon
    message="For a proof of concept only. OAuth credentials are lost whenever the gateway's pods restart, and its database and logs whenever their pods are rescheduled."
  />
);

const MISSING_DEPLOYMENT_INPUT =
  "Fill in the values above to generate the deployment Terraform.";

/** Deployment-time values that only ever feed the generated Terraform below —
 * collected as ephemeral instructions state (never persisted) rather than
 * schema fields, so they stay editable every time this screen renders
 * instead of being locked forever after a gateway's initial creation. */
const DeploymentInputs = (props: {
  gcip: boolean;
  state: Readonly<Record<string, any>>;
  setState: (value: Record<string, any>) => any;
}) => {
  const { gcip, state, setState } = props;
  const update = (key: string) => (value: any) =>
    setState({ ...state, [key]: value });
  const updateFromInput =
    (key: string) => (event: ChangeEvent<HTMLInputElement>) =>
      update(key)(event.target.value);
  const values = deploymentValuesFromState(state);
  const { storageType } = values;

  return (
    <Form layout="vertical" requiredMark="optional">
      <Form.Item
        label="Certificate contact email"
        required
        {...fieldValidation(values, "letsEncryptEmail")}
      >
        <FieldDescription>
          Receives expiry and renewal notices for your gateway&apos;s HTTPS
          certificate. Not published. The certificate is auto-generated using
          Let&apos;s Encrypt.
        </FieldDescription>
        <Input
          placeholder="ops@example.com"
          value={state.letsEncryptEmail ?? ""}
          onChange={updateFromInput("letsEncryptEmail")}
        />
      </Form.Item>
      {!gcip && (
        <>
          <Form.Item
            label="Identity provider client ID"
            required
            {...fieldValidation(values, "oidcClientId")}
          >
            <FieldDescription>
              The OAuth client ID your users sign in with, from your identity
              provider (e.g. Google Cloud Console).
            </FieldDescription>
            <Input
              value={state.oidcClientId ?? ""}
              onChange={updateFromInput("oidcClientId")}
            />
          </Form.Item>
          <Form.Item
            label="Identity provider issuer"
            {...fieldValidation(values, "oidcIssuer")}
          >
            <FieldDescription>
              The issuer URL of your identity provider. Leave blank for Google.
            </FieldDescription>
            <Input
              placeholder={GOOGLE_OIDC_ISSUER}
              value={state.oidcIssuer ?? ""}
              onChange={updateFromInput("oidcIssuer")}
            />
          </Form.Item>
        </>
      )}
      <Form.Item label="Gateway storage" required>
        <FieldDescription>
          How the gateway&apos;s bundled PostgreSQL, VictoriaLogs, and Valkey
          store their data.
        </FieldDescription>
        <Select
          className="w-full"
          value={storageType}
          onChange={update("storageType")}
          options={STORAGE_TYPE_OPTIONS}
        />
        {storageType === "noPersistence" && noPersistenceWarning}
      </Form.Item>
      {storageType === "storageClass" && (
        <Form.Item
          label="Storage class"
          required
          {...fieldValidation(values, "storageClassName")}
        >
          <FieldDescription>{storageClassNameHelp}</FieldDescription>
          <Input
            placeholder="standard-rwo"
            value={state.storageClassName ?? ""}
            onChange={updateFromInput("storageClassName")}
          />
          {UNENCRYPTED_STORAGE_CLASSES.has(values.storageClassName) &&
            unencryptedStorageClassWarning(values.storageClassName)}
        </Form.Item>
      )}
      <Form.Item
        label="Namespace"
        {...fieldValidation(values, "kubernetesNamespace")}
      >
        <FieldDescription>
          The Kubernetes namespace to deploy the gateway into. Defaults to{" "}
          <Text code>{DEFAULT_KUBERNETES_NAMESPACE}</Text> if left blank.
        </FieldDescription>
        <Input
          placeholder={DEFAULT_KUBERNETES_NAMESPACE}
          value={state.kubernetesNamespace ?? ""}
          onChange={updateFromInput("kubernetesNamespace")}
        />
      </Form.Item>
    </Form>
  );
};

/** The storage choice, or `undefined` if a storage class is chosen and its
 * name is missing or invalid. */
const gatewayStorageFrom = (
  values: DeploymentValues
): GatewayStorage | undefined => {
  if (values.storageType === "noPersistence") {
    return { type: "noPersistence" };
  }
  return isFieldValid(values, "storageClassName")
    ? { type: "storageClass", name: values.storageClassName }
    : undefined;
};

export const agenticGatewayInstaller: GatewayInstaller = {
  items: {
    "log-provider": agenticLogProviderFieldInstaller,
    braekhusPublicJwk: {
      labeler: describeBraekhusPublicJwk,
    },
    domainHosting: {
      options: {
        p0Hosted: {
          items: {
            loadBalancerIp: {
              labeler: (_context, _id, item) => {
                // Pure type-narrowing. Impossible to have reach here without a hostedDomain
                if (
                  item.domainHosting?.type !== "p0Hosted" ||
                  !item.domainHosting.hostedDomain
                ) {
                  throw new Error(
                    "Expected a p0Hosted gateway with a hostedDomain already assigned"
                  );
                }
                return describeP0HostedLoadBalancerAddress(
                  item.domainHosting.hostedDomain
                );
              },
            },
          },
        },
        selfHosted: {
          items: {
            loadBalancerIp: {
              labeler: (_context, _id, item) =>
                describeLoadBalancerAddress(loadBalancerIpOf(item)),
            },
          },
        },
      },
    },
  },
  instructions: (context, id, item, _field, state, setState) => {
    const { serviceAccountEmail } = item;
    const { orgData } = context;
    const url = gatewayUrlOf(item);
    if (!url || !orgData?.slug) {
      return {
        help: <>Complete the previous step to see deployment instructions.</>,
      };
    }
    const { slug: orgSlug } = orgData;
    const environment = getEnvironment();
    const { apiUrl, braekhusUrl } = environment;
    const p0Url = `${apiUrl}/o/${orgSlug}`;
    // Only a dev environment's audience can differ from `p0Url`: locally the
    // app talks to localhost, but P0 checks audiences against the real dev
    // host. Everywhere else the chart's default (`p0Url`) is right.
    const p0Audience =
      environment.environmentType === "devLike"
        ? `${environment.p0AudienceBase}/o/${orgSlug}`
        : undefined;
    const tunnelHost = new URL(braekhusUrl).host;
    const federatedIssuers = installedFederatedIssuers(context.config);
    const gcip = isGcipLogin(context);

    const values = deploymentValuesFromState(state);
    const storage = gatewayStorageFrom(values);

    // Password sign-in is a per-tenant GCIP policy enabled separately —
    // deriving these values here doesn't itself make the mode available.
    const login: GatewayLogin = gcip
      ? { mode: "gcip", ...getEnvGcipConfig(), tenantId: orgData.tenantId }
      : { mode: "oidc", ...values };

    const canGenerate =
      isFieldValid(values, "letsEncryptEmail") &&
      (gcip ||
        (isFieldValid(values, "oidcClientId") &&
          isFieldValid(values, "oidcIssuer"))) &&
      isFieldValid(values, "kubernetesNamespace") &&
      !!storage;

    return {
      help: (
        <>
          <p>
            Fill out the values below to generate your gateway&apos;s Terraform.
          </p>
          <p>
            Only required values are rendered. See the{" "}
            <Link
              href="https://hub.docker.com/r/p0security/agentic-gateway-stack"
              target="_blank"
              rel="noopener"
            >
              chart&apos;s full values reference
            </Link>{" "}
            to customize further (e.g. observability, backend container
            settings, or a non-default OAuth client integration).
          </p>
          <DeploymentInputs gcip={gcip} state={state} setState={setState} />
        </>
      ),
      commands: {
        iac: [
          {
            command: canGenerate
              ? gatewayDeploymentTerraform({
                  ...values,
                  federatedIssuers,
                  gatewayId: id,
                  // Blank means the default namespace, which only `undefined`
                  // selects.
                  kubernetesNamespace: values.kubernetesNamespace || undefined,
                  login,
                  orgSlug,
                  p0Audience,
                  p0Url,
                  serviceAccountEmail,
                  storage,
                  tunnelHost,
                  url,
                })
              : MISSING_DEPLOYMENT_INPUT,
          },
        ],
      },
    };
  },
};
